import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { BudgetBar } from '../components/BudgetBar';
import { PageHeader } from '@/components/layout/page-header';

export function AdminDepartmentsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin-departments'],
    queryFn: () => api.listDepartments(),
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.deleteDepartment(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-departments'] });
      qc.invalidateQueries({ queryKey: ['departments-lookup'] });
    },
  });
  const reorder = useMutation({
    mutationFn: (ids: number[]) => api.reorderDepartments(ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-departments'] });
      qc.invalidateQueries({ queryKey: ['departments-lookup'] });
    },
  });

  function move(idx: number, direction: -1 | 1) {
    if (!data) return;
    const ids = data.rows.map((d) => d.id);
    const target = idx + direction;
    if (target < 0 || target >= ids.length) return;
    [ids[idx], ids[target]] = [ids[target]!, ids[idx]!];
    reorder.mutate(ids);
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Departments"
        actions={
          <Link
            to="/admin/departments/new"
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <Plus size={14} strokeWidth={1.75} />
            Add department
          </Link>
        }
      />
      {isLoading && <Skeleton className="h-40 w-full" />}
      {data && (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-x-auto rounded-lg border border-border bg-card md:block">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 border-b border-border bg-background text-left">
                <tr>
                  <th className="w-16 p-3 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    Order
                  </th>
                  <th className="p-3 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    Code
                  </th>
                  <th className="p-3 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    Name
                  </th>
                  <th className="p-3 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    Spent / Budget
                  </th>
                  <th className="p-3 text-right font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    Tx this month
                  </th>
                  <th className="p-3"> </th>
                </tr>
              </thead>
              <tbody className="[&_tr:nth-child(even)]:bg-background">
                {data.rows.map((d, idx) => (
                  <tr
                    key={d.id}
                    className="border-b border-border last:border-0 hover:bg-background"
                  >
                    <td className="p-3">
                      <div className="flex items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => move(idx, -1)}
                          disabled={idx === 0 || reorder.isPending}
                          className="rounded p-1 text-muted-foreground hover:bg-background hover:text-foreground disabled:opacity-30 disabled:hover:bg-transparent"
                          aria-label="Move up"
                        >
                          <ChevronUp size={14} strokeWidth={1.75} />
                        </button>
                        <button
                          type="button"
                          onClick={() => move(idx, 1)}
                          disabled={idx === data.rows.length - 1 || reorder.isPending}
                          className="rounded p-1 text-muted-foreground hover:bg-background hover:text-foreground disabled:opacity-30 disabled:hover:bg-transparent"
                          aria-label="Move down"
                        >
                          <ChevronDown size={14} strokeWidth={1.75} />
                        </button>
                      </div>
                    </td>
                    <td className="p-3 font-mono text-muted-foreground">{d.code}</td>
                    <td className="p-3 text-foreground">{d.name}</td>
                    <td className="min-w-[220px] p-3">
                      <BudgetBar spent={d.spent_this_month_vnd} budget={d.budget_monthly_vnd} />
                    </td>
                    <td className="p-3 text-right tabular-nums text-muted-foreground">
                      {d.tx_count_this_month}
                    </td>
                    <td className="p-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/admin/departments/${d.id}/edit`)}
                        className="mr-1 text-primary"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (confirm(`Soft-delete ${d.code}?`)) remove.mutate(d.id);
                        }}
                        className="text-destructive"
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="space-y-2 md:hidden">
            {data.rows.map((d, idx) => (
              <div key={d.id} className="rounded-lg border border-border bg-card p-3 text-sm">
                <div className="mb-2 flex items-center justify-between">
                  <span className="rounded bg-background px-2 py-0.5 font-mono text-xs text-muted-foreground">
                    {d.code}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0 || reorder.isPending}
                      className="rounded p-1 text-muted-foreground hover:bg-background disabled:opacity-30"
                      aria-label="Move up"
                    >
                      <ChevronUp size={14} strokeWidth={1.75} />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(idx, 1)}
                      disabled={idx === data.rows.length - 1 || reorder.isPending}
                      className="rounded p-1 text-muted-foreground hover:bg-background disabled:opacity-30"
                      aria-label="Move down"
                    >
                      <ChevronDown size={14} strokeWidth={1.75} />
                    </button>
                    <span className="ml-2 text-xs text-muted-foreground">
                      {d.tx_count_this_month} tx/month
                    </span>
                  </div>
                </div>
                <div className="mb-2 font-medium text-foreground">{d.name}</div>
                <div className="mb-3">
                  <BudgetBar spent={d.spent_this_month_vnd} budget={d.budget_monthly_vnd} compact />
                </div>
                <div className="flex gap-2">
                  <Link
                    to={`/admin/departments/${d.id}/edit`}
                    className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10"
                  >
                    <Pencil size={12} strokeWidth={1.75} /> Edit
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm(`Soft-delete ${d.code}?`)) remove.mutate(d.id);
                    }}
                    className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 size={12} strokeWidth={1.75} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
